import { EMPTY_BASIC_INFO, EMPTY_OVERVIEW, createEmptyBuildingUnit } from "./constants";
import { renderAsync as renderDocxAsync } from "docx-preview";
import { renderAdminUsersPage } from "./pages/admin-users-page";
import { renderInspectionItemDetailPage } from "./pages/inspection-item-detail-page";
import { renderInspectionItemsPage } from "./pages/inspection-items-page";
import { renderInspectionSubitemEditPage } from "./pages/inspection-subitem-edit-page";
import { renderLoginPage } from "./pages/login-page";
import { renderNewProjectPage } from "./pages/new-project-page";
import { renderProjectBasicInfoPage } from "./pages/project-basic-info-page";
import { renderProjectDeclarationPage } from "./pages/project-declaration-page";
import { renderProjectHomePage } from "./pages/project-home-page";
import { renderProjectListPage } from "./pages/project-list-page";
import { renderProjectPreviewPage } from "./pages/project-preview-page";
import { renderProjectRecordPreviewPage } from "./pages/project-record-preview-page";
import { renderProjectReportPreviewPage } from "./pages/project-report-preview-page";
import { renderProjectOverviewPage } from "./pages/project-overview-page";
import { renderProjectReceivingPage } from "./pages/project-receiving-page";
import { deletePhotoRefs, persistImageDataUrls, preloadPhotoRefs, resolvePhotoUrl } from "./photo-storage";
import { buildRecordPreviewRows, buildReportPreview } from "./report-preview";
import { buildExportReadiness } from "./project-readiness";
import { getCurrentRoute, navigate } from "./router";
import type { InspectionSubitemData } from "./types";
import { syncProjectSnapshot } from "./sync-api";
import {
  addManagedUser,
  addGroup,
  createProject,
  deleteManagedUser,
  deleteProject,
  flushServerState,
  getPendingSyncChanges,
  getProjectPendingSyncCount,
  getInspectionItem,
  getInspectionSubitem,
  getProjectById,
  getState,
  initializeServerState,
  listGroups,
  listProjects,
  listUsers,
  loginAdmin,
  loginManagedUser,
  logout,
  markProjectSynced,
  saveDeclarationData,
  saveInspectionItemDecision,
  saveInspectionSubitem,
  migrateLegacyPhotoStorage,
  clearExportGenerated,
  markExportGenerated,
  markPreviewGenerated,
  renameProject,
  saveProjectBasicInfo,
  saveProjectOverview,
  saveReceivingData,
  setCurrentProject,
  updateManagedUserGroup,
  updateProjectGroup,
} from "./store";

function getReportProjectName(project: ReturnType<typeof getProjectById>): string {
  if (!project) {
    return "未命名项目";
  }
  return project.basicInfo.projectName.trim() || project.name;
}

function buildEmptyRecognitionBasicInfo() {
  return {
    ...EMPTY_BASIC_INFO,
    constructionUnits: [""],
    designCompanies: [""],
    constructionCompanies: [""],
    supervisionCompanies: [""],
  };
}

function buildEmptyRecognitionOverview(preservedFireFacilitySystems: string[]) {
  return {
    ...EMPTY_OVERVIEW,
    buildingUnits: [createEmptyBuildingUnit()],
    fireFacilitySystems: [...preservedFireFacilitySystems],
  };
}

function resetDeclarationDrivenProjectInfo(projectId: string): void {
  const project = getProjectById(projectId);
  if (!project) {
    return;
  }

  saveProjectBasicInfo(projectId, buildEmptyRecognitionBasicInfo());
  saveProjectOverview(projectId, buildEmptyRecognitionOverview(project.overview.fireFacilitySystems));
}

function getExportDownloadUrl(exportRef: string): string {
  return `/downloads/${exportRef}`;
}

function getPreviewHeading(kind: "report" | "record"): string {
  return kind === "report" ? "报告成品预览" : "记录表成品预览";
}

function formatExportTimestamp(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
}

function buildExportFileName(projectName: string, kind: "report" | "record", preview: boolean): string {
  const suffix =
    kind === "report"
      ? preview
        ? "消防验收报告成品预览"
        : "消防验收报告"
      : preview
        ? "现场评定记录表成品预览"
        : "现场评定记录表";
  return `${projectName}-${suffix}-${formatExportTimestamp()}.docx`;
}

function fitGeneratedPreviewToViewport(frameNode: HTMLElement): void {
  const wrapper = frameNode.querySelector<HTMLElement>(".docx-preview-page-wrapper");
  if (!wrapper) {
    return;
  }

  const availableWidth = Math.max(frameNode.clientWidth - 20, 280);
  wrapper.style.transform = "";
  wrapper.style.width = "";
  frameNode.style.minHeight = "";

  const wrapperWidth = wrapper.scrollWidth;
  const wrapperHeight = wrapper.scrollHeight;
  if (!wrapperWidth || !wrapperHeight) {
    return;
  }

  const scale = Math.min(1, availableWidth / wrapperWidth);
  wrapper.style.width = `${wrapperWidth}px`;

  if (scale < 0.999) {
    wrapper.style.transform = `scale(${scale})`;
    wrapper.style.transformOrigin = "top center";
    frameNode.style.minHeight = `${Math.ceil(wrapperHeight * scale) + 12}px`;
    return;
  }

  frameNode.style.minHeight = `${wrapperHeight + 12}px`;
}

function bindGeneratedPreviewResize(frameNode: HTMLElement): void {
  const marker = "__generatedPreviewResizeBound";
  if ((frameNode as HTMLElement & { [marker]?: boolean })[marker]) {
    return;
  }

  const onResize = () => fitGeneratedPreviewToViewport(frameNode);
  window.addEventListener("resize", onResize);
  (frameNode as HTMLElement & { [marker]?: boolean })[marker] = true;
}

async function buildResolvedExportPayload(project: NonNullable<ReturnType<typeof getProjectById>>, generatedAt: string) {
  await preloadPhotoRefs(collectProjectPhotoRefs(project));

  const resolvedReceiving = {
    ...project.receiving,
    photos: project.receiving.photos.map((photo) => resolvePhotoUrl(photo)).filter(Boolean),
  };
  const resolvedInspectionItems = project.inspectionItems.map((item) => ({
    ...item,
    subitems: item.subitems.map((subitem) => ({
      ...subitem,
      photos: subitem.photos.map((photo) => resolvePhotoUrl(photo)).filter(Boolean),
    })),
  }));
  const exportProjectName = getReportProjectName(project);

  return {
    projectName: exportProjectName,
    generatedAt,
    basicInfo: project.basicInfo,
    overview: project.overview,
    receiving: resolvedReceiving,
    declaration: project.declaration,
    inspectionItems: resolvedInspectionItems,
    sections: buildReportPreview(project),
    rows: buildRecordPreviewRows(project),
  };
}

function ensureAppElement(): HTMLElement {
  const app = document.querySelector<HTMLElement>("#app");
  if (!app) {
    throw new Error("App root not found.");
  }
  return app;
}

function redirectForSession(): void {
  const { user } = getState();
  const route = getCurrentRoute();

  if (!user && route.name !== "login") {
    navigate("#/login");
    return;
  }

  if (user && route.name === "login") {
    navigate("#/projects");
  }
}

function collectProjectPhotoRefs(project: ReturnType<typeof getProjectById>): string[] {
  if (!project) {
    return [];
  }

  return [
    ...[project.declaration.pdfFileDataUrl].filter(Boolean),
    ...project.receiving.photos,
    ...project.declaration.photoDataUrls,
    ...project.inspectionItems.flatMap((item) => item.subitems.flatMap((subitem) => subitem.photos)),
  ];
}

async function render(): Promise<void> {
  try {
    redirectForSession();

    const app = ensureAppElement();
    const route = getCurrentRoute();
    let { user } = getState();

    if (!user || route.name === "login") {
      app.innerHTML = renderLoginPage();
      bindLoginForm();
      return;
    }

    if (route.name === "admin-users") {
      if (user.role !== "admin") {
        navigate("#/projects");
        return;
      }
      await initializeServerState();
      user = getState().user;
      if (!user || user.role !== "admin") {
        navigate("#/login");
        return;
      }
      app.innerHTML = renderAdminUsersPage(listUsers(), listGroups());
      bindAdminUsersPage();
      return;
    }

    if (route.name === "project-list") {
      await initializeServerState();
      user = getState().user;
      if (!user) {
        navigate("#/login");
        return;
      }
      const projects = listProjects();
      const pendingCountByProject = Object.fromEntries(
        projects.map((project) => [project.id, getProjectPendingSyncCount(project.id)]),
      );
      app.innerHTML = renderProjectListPage(user, projects, pendingCountByProject, listGroups());
      bindProjectListPage();
      return;
    }

    if (route.name === "new-project") {
      app.innerHTML = renderNewProjectPage();
      bindNewProjectPage();
      return;
    }

    if (!route.projectId) {
      navigate("#/projects");
      return;
    }

    const project = getProjectById(route.projectId);
    if (!project) {
      navigate("#/projects");
      return;
    }

    await preloadPhotoRefs(collectProjectPhotoRefs(project));

    setCurrentProject(project.id);
    const pendingCount = getProjectPendingSyncCount(project.id);
    const exportReadiness = buildExportReadiness(project, pendingCount);

    if (route.name === "project-home") {
      app.innerHTML = renderProjectHomePage(project, pendingCount);
      bindProjectHomePage(project.id);
      return;
    }

    if (route.name === "project-declaration") {
      app.innerHTML = renderProjectDeclarationPage(project, pendingCount, route);
      bindDeclarationPage(project.id, route.source);
      return;
    }

    if (route.name === "project-basic-info") {
      app.innerHTML = renderProjectBasicInfoPage(project);
      bindBasicInfoPage(project.id);
      return;
    }

    if (route.name === "project-overview") {
      app.innerHTML = renderProjectOverviewPage(project);
      bindProjectOverviewPage(project.id);
      return;
    }

    if (route.name === "project-receiving") {
      app.innerHTML = renderProjectReceivingPage(project);
      bindReceivingPage(project.id);
      return;
    }

    if (route.name === "project-preview") {
      app.innerHTML = renderProjectPreviewPage(project, pendingCount);
      bindProjectPreviewPage(project.id);
      return;
    }

    if (route.name === "project-report-preview") {
      app.innerHTML = renderProjectReportPreviewPage(project);
      bindProjectReportPreviewPage(project.id);
      return;
    }

    if (route.name === "project-record-preview") {
      app.innerHTML = renderProjectRecordPreviewPage(project);
      bindProjectRecordPreviewPage(project.id);
      return;
    }

    if (route.name === "inspection-items") {
      app.innerHTML = renderInspectionItemsPage(project);
      bindInspectionItemsPage(project.id);
      return;
    }

    if (route.name === "inspection-item-detail") {
      if (!route.itemId) {
        navigate(`#/projects/inspection-items?id=${project.id}`);
        return;
      }
      const item = getInspectionItem(project.id, route.itemId);
      if (!item) {
        navigate(`#/projects/inspection-items?id=${project.id}`);
        return;
      }
      app.innerHTML = renderInspectionItemDetailPage(project.id, project.name, item);
      bindInspectionItemDetailPage(project.id, item.itemId);
      return;
    }

    if (route.name === "inspection-subitem-edit") {
      if (!route.itemId || !route.subitemId) {
        navigate(`#/projects/inspection-items?id=${project.id}`);
        return;
      }
      const subitem = getInspectionSubitem(project.id, route.itemId, route.subitemId);
      if (!subitem) {
        navigate(`#/projects/inspection-item?projectId=${project.id}&itemId=${route.itemId}`);
        return;
      }
      app.innerHTML = renderInspectionSubitemEditPage(project.name, project.id, subitem);
      bindInspectionSubitemEditPage(project.id, route.itemId, route.subitemId);
      return;
    }

    navigate("#/projects");
  } catch (error) {
    console.error(error);
    const app = ensureAppElement();
    app.innerHTML = `
      <section class="screen">
        <div class="panel stack">
          <h1>页面加载失败</h1>
          <p class="muted">当前页面出现异常，已保留本地数据。请返回项目列表后重新进入。</p>
          <button class="primary-button" type="button" data-action="fallback-back-to-list">返回项目列表</button>
        </div>
      </section>
    `;
    document.querySelector<HTMLElement>("[data-action='fallback-back-to-list']")?.addEventListener("click", () => {
      navigate("#/projects");
    });
  }
}

function bindLoginForm(): void {
  const errorNode = document.querySelector<HTMLElement>("#login-error");
  const showError = (message: string) => {
    if (!errorNode) {
      return;
    }
    errorNode.hidden = false;
    errorNode.textContent = message;
    errorNode.scrollIntoView({ block: "nearest" });
  };

  const userForm = document.querySelector<HTMLFormElement>("#user-login-form");
  userForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    const formData = new FormData(userForm);
    const name = String(formData.get("name") || "").trim();
    const password = String(formData.get("password") || "").trim();
    if (!name || !password) {
      return;
    }
    const result = loginManagedUser(name, password);
    if (!result.ok) {
      showError(result.message);
      return;
    }
    navigate("#/projects");
  });

  const adminForm = document.querySelector<HTMLFormElement>("#admin-login-form");
  adminForm?.addEventListener("submit", (event) => {
    event.preventDefault();
    const formData = new FormData(adminForm);
    const account = String(formData.get("account") || "").trim();
    const password = String(formData.get("password") || "").trim();
    if (!account || !password) {
      return;
    }
    const result = loginAdmin(account, password);
    if (!result.ok) {
      showError(result.message);
      return;
    }
    navigate("#/admin/users");
  });
}

function bindProjectListPage(): void {
  document.querySelector<HTMLElement>("[data-action='logout']")?.addEventListener("click", () => {
    logout();
    navigate("#/login");
  });

  document.querySelector<HTMLElement>("[data-action='open-admin-users']")?.addEventListener("click", () => {
    navigate("#/admin/users");
  });

  document.querySelectorAll<HTMLElement>("[data-action='create-project']").forEach((node) => {
    node.addEventListener("click", () => navigate("#/projects/new"));
  });

  document.querySelectorAll<HTMLElement>("[data-action='open-project']").forEach((node) => {
    node.addEventListener("click", () => {
      const projectId = node.dataset.projectId;
      if (!projectId) {
        return;
      }
      navigate(`#/projects/home?id=${projectId}`);
    });
  });

  document.querySelectorAll<HTMLElement>("[data-action='delete-project']").forEach((node) => {
    node.addEventListener("click", () => {
      const projectId = node.dataset.projectId;
      const projectName = node.dataset.projectName || "当前项目";
      if (!projectId) {
        return;
      }
      const confirmed = window.confirm(`确认删除此项目“${projectName}”吗？`);
      if (!confirmed) {
        return;
      }
      try {
        deleteProject(projectId);
        render();
      } catch (error) {
        window.alert(error instanceof Error ? error.message : "删除项目失败，请稍后重试。");
      }
    });
  });

  bindGroupSelectFields(document);

  document.querySelectorAll<HTMLElement>("[data-action='save-project-group']").forEach((node) => {
    node.addEventListener("click", () => {
      const projectId = node.dataset.projectId;
      const projectName = node.dataset.projectName || "当前项目";
      if (!projectId) {
        return;
      }
      const input = document.querySelector<HTMLInputElement>(`[data-project-group-input='${projectId}']`);
      const groupName = input?.value.trim() || "";
      const confirmed = window.confirm(`确认把项目“${projectName}”调整到小组“${groupName || "未填写"}”吗？`);
      if (!confirmed) {
        return;
      }
      const result = updateProjectGroup(projectId, groupName);
      if (!result.ok) {
        window.alert(result.message);
        return;
      }
      render();
    });
  });
}

function bindAdminUsersPage(): void {
  document.querySelector<HTMLElement>("[data-action='back-to-projects']")?.addEventListener("click", () => {
    navigate("#/projects");
  });

  const statusNode = document.querySelector<HTMLElement>("#admin-user-status");
  const groupStatusNode = document.querySelector<HTMLElement>("#admin-group-status");
  const showStatus = (message: string, tone: "success" | "error") => {
    if (!statusNode) {
      return;
    }
    statusNode.textContent = message;
    statusNode.classList.toggle("is-error", tone === "error");
    statusNode.classList.toggle("is-success", tone === "success");
  };
  const showGroupStatus = (message: string, tone: "success" | "error") => {
    if (!groupStatusNode) {
      return;
    }
    groupStatusNode.textContent = message;
    groupStatusNode.classList.toggle("is-error", tone === "error");
    groupStatusNode.classList.toggle("is-success", tone === "success");
  };

  const form = document.querySelector<HTMLFormElement>("#admin-user-form");
  const groupForm = document.querySelector<HTMLFormElement>("#admin-group-form");
  bindGroupSelectFields(document);

  groupForm?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formData = new FormData(groupForm);
    const groupName = String(formData.get("groupName") || "").trim();
    const confirmed = window.confirm(`确认新增小组“${groupName || "未填写"}”吗？`);
    if (!confirmed) {
      return;
    }
    const result = addGroup(groupName);
    if (!result.ok) {
      showGroupStatus(result.message, "error");
      return;
    }
    showGroupStatus("小组已新增。", "success");
    groupForm.reset();
    await flushServerState();
    render();
  });

  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formData = new FormData(form);
    const name = String(formData.get("name") || "").trim();
    const groupName = String(formData.get("groupName") || "").trim();
    const confirmed = window.confirm(`确认添加此用户吗？\n姓名：${name || "未填写"}\n小组：${groupName || "未填写"}`);
    if (!confirmed) {
      return;
    }
    const result = addManagedUser({
      name,
      password: String(formData.get("password") || ""),
      groupName,
    });
    if (!result.ok) {
      showStatus(result.message, "error");
      return;
    }
    showStatus("用户已添加。", "success");
    form.reset();
    await flushServerState();
    render();
  });

  document.querySelectorAll<HTMLElement>("[data-action='save-managed-user-group']").forEach((node) => {
    node.addEventListener("click", async () => {
      const userId = node.dataset.userId;
      const userName = node.dataset.userName || "当前用户";
      if (!userId) {
        return;
      }
      const input = document.querySelector<HTMLInputElement>(`[data-user-group-input='${userId}']`);
      const groupName = input?.value.trim() || "";
      const confirmed = window.confirm(`确认把用户“${userName}”调整到小组“${groupName || "未填写"}”吗？`);
      if (!confirmed) {
        return;
      }
      const result = updateManagedUserGroup(userId, groupName);
      if (!result.ok) {
        showStatus(result.message, "error");
        return;
      }
      showStatus(`已更新“${userName}”的小组。`, "success");
      await flushServerState();
      render();
    });
  });

  document.querySelectorAll<HTMLElement>("[data-action='delete-managed-user']").forEach((node) => {
    node.addEventListener("click", () => {
      const userId = node.dataset.userId;
      const userName = node.dataset.userName || "当前用户";
      if (!userId) {
        return;
      }
      const confirmed = window.confirm(`确认删除此用户“${userName}”吗？`);
      if (!confirmed) {
        return;
      }
      deleteManagedUser(userId);
      render();
    });
  });
}

function bindGroupSelectFields(root: ParentNode): void {
  root.querySelectorAll<HTMLSelectElement>("[data-action='copy-group-option']").forEach((selectNode) => {
    if (selectNode.dataset.groupSelectBound === "true") {
      return;
    }
    selectNode.dataset.groupSelectBound = "true";
    selectNode.addEventListener("change", () => {
      const value = selectNode.value.trim();
      if (!value) {
        return;
      }
      const targetId = selectNode.dataset.targetInput;
      if (!targetId) {
        return;
      }
      const targetInput = document.getElementById(targetId) as HTMLInputElement | null;
      if (!targetInput) {
        return;
      }
      targetInput.value = value;
      selectNode.value = "";
      targetInput.dispatchEvent(new Event("input", { bubbles: true }));
    });
  });
}

function bindNewProjectPage(): void {
  document.querySelector<HTMLElement>("[data-action='back-to-list']")?.addEventListener("click", () => {
    navigate("#/projects");
  });

  const form = document.querySelector<HTMLFormElement>("#new-project-form");
  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    const formData = new FormData(form);
    const projectName = String(formData.get("projectName") || "").trim();
    if (!projectName) {
      return;
    }
    try {
      const project = createProject(projectName);
      navigate(`#/projects/home?id=${project.id}`);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "创建项目失败，请稍后重试。");
    }
  });
}

function bindProjectHomePage(projectId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-list']")?.addEventListener("click", () => {
    navigate("#/projects");
  });

  document.querySelector<HTMLElement>("[data-action='rename-project']")?.addEventListener("click", () => {
    const project = getProjectById(projectId);
    if (!project) {
      return;
    }

    const nextName = window.prompt("请输入项目标题", project.name);
    if (nextName === null) {
      return;
    }

    const trimmedName = nextName.trim();
    if (!trimmedName || trimmedName === project.name) {
      return;
    }

    renameProject(projectId, trimmedName);
    render();
  });

  document.querySelector<HTMLElement>("[data-action='go-project-info']")?.addEventListener("click", () => {
    navigate(`#/projects/basic-info?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='go-inspection-items']")?.addEventListener("click", () => {
    navigate(`#/projects/inspection-items?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='go-preview']")?.addEventListener("click", () => {
    navigate(`#/projects/preview?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='go-receiving']")?.addEventListener("click", () => {
    navigate(`#/projects/receiving?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='sync-project']")?.addEventListener("click", async () => {
    await syncCurrentProject(projectId);
  });
}

function bindProjectPreviewPage(projectId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-home']")?.addEventListener("click", () => {
    navigate(`#/projects/home?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='go-report-preview']")?.addEventListener("click", () => {
    navigate(`#/projects/report-preview?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='go-record-preview']")?.addEventListener("click", () => {
    navigate(`#/projects/record-preview?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='export-report-docx']")?.addEventListener("click", async () => {
    await exportProjectWord(projectId, "report");
  });

  document.querySelector<HTMLElement>("[data-action='export-record-docx']")?.addEventListener("click", async () => {
    await exportProjectWord(projectId, "record");
  });

  document.querySelector<HTMLElement>("[data-action='clear-report-export']")?.addEventListener("click", () => {
    clearProjectExport(projectId, "report");
  });

  document.querySelector<HTMLElement>("[data-action='clear-record-export']")?.addEventListener("click", () => {
    clearProjectExport(projectId, "record");
  });

  document.querySelector<HTMLElement>("[data-action='send-export-email']")?.addEventListener("click", async () => {
    await sendExportEmail(projectId);
  });

  document.querySelector<HTMLElement>("[data-action='sync-project']")?.addEventListener("click", async () => {
    await syncCurrentProject(projectId);
  });
}

function bindProjectReportPreviewPage(projectId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-preview-home']")?.addEventListener("click", () => {
    navigate(`#/projects/preview?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='refresh-generated-preview']")?.addEventListener("click", async () => {
    await renderGeneratedDocxPreview(projectId, "report", true);
  });

  void renderGeneratedDocxPreview(projectId, "report", true);
}

function bindProjectRecordPreviewPage(projectId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-preview-home']")?.addEventListener("click", () => {
    navigate(`#/projects/preview?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='refresh-generated-preview']")?.addEventListener("click", async () => {
    await renderGeneratedDocxPreview(projectId, "record", true);
  });

  void renderGeneratedDocxPreview(projectId, "record", true);
}

function bindDeclarationPage(projectId: string, source?: "basic-info"): void {
  const showDeclarationError = (error: unknown) => {
    const message = error instanceof Error ? error.message : "申报表处理失败，请稍后重试。";
    window.alert(message);
  };

  document.querySelector<HTMLElement>("[data-action='back-to-home']")?.addEventListener("click", () => {
    navigate(source === "basic-info" ? `#/projects/basic-info?id=${projectId}` : `#/projects/home?id=${projectId}`);
    void render();
  });

  document.querySelector<HTMLElement>("[data-action='go-basic-info']")?.addEventListener("click", () => {
    navigate(`#/projects/basic-info?id=${projectId}`);
    void render();
  });

  document.querySelector<HTMLElement>("[data-action='run-declaration-recognition']")?.addEventListener("click", async () => {
    await runDeclarationRecognition(projectId);
  });

  document.querySelector<HTMLElement>("[data-action='apply-declaration-recognition']")?.addEventListener("click", () => {
    applyDeclarationRecognition(projectId);
  });

  document.querySelector<HTMLElement>("[data-action='skip-declaration-upload']")?.addEventListener("click", () => {
    const project = getProjectById(projectId);
    if (!project) {
      return;
    }

    resetDeclarationDrivenProjectInfo(projectId);
    saveDeclarationData(projectId, {
      ...project.declaration,
      entryMode: "skip",
      skipped: true,
      parseStatus: "skipped",
      sourceSummary: "用户选择先跳过申报表上传，后续可补传 PDF 或拍照件。",
      recognition: {
        ...project.declaration.recognition,
        status: "skipped",
        summary: "当前已选择稍后上传申请表，识别流程暂不启动。",
        rawText: "",
        updatedAt: new Date().toLocaleString("zh-CN", { hour12: false }),
        extractedBasicInfo: {},
        extractedOverview: {},
      },
    });
    render();
  });

  document.querySelector<HTMLElement>("[data-action='clear-declaration-skip']")?.addEventListener("click", () => {
    const project = getProjectById(projectId);
    if (!project) {
      return;
    }

    saveDeclarationData(projectId, {
      ...project.declaration,
      entryMode: "none",
      skipped: false,
      parseStatus: "idle",
      sourceSummary: "",
      recognition: {
        ...project.declaration.recognition,
        status: "idle",
        summary: "尚未开始识别。",
        rawText: "",
        updatedAt: "",
        extractedBasicInfo: {},
        extractedOverview: {},
      },
    });
    render();
  });

  document.querySelector<HTMLElement>("[data-action='clear-declaration-pdf']")?.addEventListener("click", () => {
    const project = getProjectById(projectId);
    if (!project) {
      return;
    }

    const previousPdfValue = project.declaration.pdfFileDataUrl;

    try {
      resetDeclarationDrivenProjectInfo(projectId);
      saveDeclarationData(projectId, {
        ...project.declaration,
        pdfFileName: "",
        pdfFileDataUrl: "",
        sourceSummary: project.declaration.photoDataUrls.length
          ? "已保留申报表照片，可继续人工校对。"
          : "当前未导入申报表文件。",
        parseStatus: project.declaration.photoDataUrls.length ? "ready_for_review" : "idle",
        entryMode: project.declaration.photoDataUrls.length ? "photo" : "none",
        skipped: false,
        recognition: {
          ...project.declaration.recognition,
          status: project.declaration.photoDataUrls.length ? "ready_for_review" : "idle",
          summary: project.declaration.photoDataUrls.length
            ? "申报表材料已变更，可以重新开始本地识别。"
            : "尚未开始识别。",
          rawText: "",
          updatedAt: "",
          extractedBasicInfo: {},
          extractedOverview: {},
        },
      });
      void deletePhotoRefs(previousPdfValue ? [previousPdfValue] : []);
      render();
    } catch (error) {
      showDeclarationError(error);
    }
  });

  document.querySelector<HTMLInputElement>("#declaration-pdf-input")?.addEventListener("change", async (event) => {
    const target = event.currentTarget as HTMLInputElement;
    const file = target.files?.[0];
    const project = getProjectById(projectId);
    if (!file || !project) {
      return;
    }

    try {
      const [dataUrl] = await readFilesAsDataUrls(target.files);
      const previousPdfValue = project.declaration.pdfFileDataUrl;
      const previousPhotoValues = [...project.declaration.photoDataUrls];
      resetDeclarationDrivenProjectInfo(projectId);
      saveDeclarationData(projectId, {
        ...project.declaration,
        entryMode: "pdf",
        pdfFileName: file.name,
        pdfFileDataUrl: dataUrl || "",
        photoNames: [],
        photoDataUrls: [],
        skipped: false,
        parseStatus: "ready_for_review",
        sourceSummary: "已上传验收申请表 PDF，识别后会自动写入项目基本信息与项目概况。",
        recognition: {
          ...project.declaration.recognition,
          status: "ready_for_review",
          summary: "PDF 已上传，可以开始本地识别。",
          rawText: "",
          updatedAt: "",
          extractedBasicInfo: {},
          extractedOverview: {},
        },
      });
      void deletePhotoRefs([previousPdfValue, ...previousPhotoValues].filter(Boolean));
      render();
    } catch (error) {
      showDeclarationError(error);
    }
  });

  document.querySelector<HTMLInputElement>("#declaration-photo-input")?.addEventListener("change", async (event) => {
    const target = event.currentTarget as HTMLInputElement;
    const project = getProjectById(projectId);
    if (!project || !target.files?.length) {
      return;
    }

    try {
      const dataUrls = await readFilesAsDataUrls(target.files);
      const names = Array.from(target.files).map((file) => file.name);
      const previousPdfValue = project.declaration.pdfFileDataUrl;
      const previousPhotoValues = [...project.declaration.photoDataUrls];
      resetDeclarationDrivenProjectInfo(projectId);
      saveDeclarationData(projectId, {
        ...project.declaration,
        entryMode: "photo",
        pdfFileName: "",
        pdfFileDataUrl: "",
        skipped: false,
        photoNames: names,
        photoDataUrls: dataUrls,
        parseStatus: "ready_for_review",
        sourceSummary: "已上传验收申请表照片，识别后会自动写入项目基本信息与项目概况。",
        recognition: {
          ...project.declaration.recognition,
          status: "ready_for_review",
          summary: "照片已上传，可以开始本地识别。",
          rawText: "",
          updatedAt: "",
          extractedBasicInfo: {},
          extractedOverview: {},
        },
      });
      void deletePhotoRefs([previousPdfValue, ...previousPhotoValues].filter(Boolean));
      render();
    } catch (error) {
      showDeclarationError(error);
    }
  });

  document.querySelectorAll<HTMLElement>("[data-declaration-photo-delete]").forEach((node) => {
    node.addEventListener("click", () => {
      const photoIndex = Number(node.dataset.declarationPhotoDelete);
      const project = getProjectById(projectId);
      if (!project || Number.isNaN(photoIndex)) {
        return;
      }

      const nextPhotoNames = project.declaration.photoNames.filter((_, index) => index !== photoIndex);
      const removedPhoto = project.declaration.photoDataUrls[photoIndex];
      const nextPhotoDataUrls = project.declaration.photoDataUrls.filter((_, index) => index !== photoIndex);
      void deletePhotoRefs(removedPhoto ? [removedPhoto] : []);
      resetDeclarationDrivenProjectInfo(projectId);
      saveDeclarationData(projectId, {
        ...project.declaration,
        photoNames: nextPhotoNames,
        photoDataUrls: nextPhotoDataUrls,
        entryMode: project.declaration.pdfFileName ? "pdf" : nextPhotoDataUrls.length ? "photo" : "none",
        parseStatus: project.declaration.pdfFileName || nextPhotoDataUrls.length ? "ready_for_review" : "idle",
        recognition: {
          ...project.declaration.recognition,
          status: project.declaration.pdfFileName || nextPhotoDataUrls.length ? "ready_for_review" : "idle",
          summary:
            project.declaration.pdfFileName || nextPhotoDataUrls.length
              ? "申报表材料已变更，可以重新开始本地识别。"
              : "尚未开始识别。",
          rawText: "",
          updatedAt: "",
          extractedBasicInfo: {},
          extractedOverview: {},
        },
      });
      render();
    });
  });

  bindImagePreview();
  bindPhotoReorder(projectId);
}

function bindBasicInfoPage(projectId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-home']")?.addEventListener("click", () => {
    navigate(`#/projects/home?id=${projectId}`);
  });

  const form = document.querySelector<HTMLFormElement>("#basic-info-form");
  const statusNode = document.querySelector<HTMLElement>("#basic-info-save-status");
  let statusTimer: number | null = null;

  const showSaveStatus = (message: string, tone: "success" | "error") => {
    if (!statusNode) {
      return;
    }

    statusNode.textContent = message;
    statusNode.classList.add("is-visible", tone === "success" ? "is-success" : "is-error");
    statusNode.classList.remove(tone === "success" ? "is-error" : "is-success");

    if (statusTimer !== null) {
      window.clearTimeout(statusTimer);
    }

    if (tone === "success") {
      statusTimer = window.setTimeout(() => {
        statusNode.classList.remove("is-visible", "is-success", "is-error");
        statusNode.textContent = "";
        statusTimer = null;
      }, 2200);
    }
  };

  const saveCurrentForm = (options?: { successMessage?: string }) => {
    if (!form) {
      return false;
    }
    try {
      const formData = new FormData(form);
      saveProjectBasicInfo(projectId, {
        projectCategory: String(formData.get("projectCategory") || ""),
        projectName: String(formData.get("projectName") || ""),
        projectAddress: String(formData.get("projectAddress") || ""),
        constructionNature: formData
          .getAll("constructionNature")
          .map((value) => String(value))
          .filter(Boolean),
        constructionUnits: readNamedValues(form, "constructionUnits"),
        designCompanies: readNamedValues(form, "designCompanies"),
        constructionCompanies: readNamedValues(form, "constructionCompanies"),
        supervisionCompanies: readNamedValues(form, "supervisionCompanies"),
      });

      const buildingUnitIds = Array.from(
        form.querySelectorAll<HTMLInputElement>("input[name='buildingUnitId']"),
      ).map((node) => node.value);
      const buildingNames = readNamedValues(form, "buildingName");
      const structureTypes = readNamedValues(form, "structureType");
      const usageTypes = readNamedValues(form, "usageType");
      const fireResistanceLevels = readNamedValues(form, "fireResistanceLevel");
      const aboveGroundFloorCounts = readNamedValues(form, "aboveGroundFloorCount");
      const undergroundFloorCounts = readNamedValues(form, "undergroundFloorCount");
      const buildingHeights = readNamedValues(form, "buildingHeight");
      const aboveGroundBuildingAreas = readNamedValues(form, "aboveGroundBuildingArea");
      const undergroundBuildingAreas = readNamedValues(form, "undergroundBuildingArea");

      saveProjectOverview(projectId, {
        buildingUnits: buildingUnitIds.map((id, index) => ({
          id,
          buildingName: buildingNames[index] || "",
          structureType: structureTypes[index] || "",
          usageType: usageTypes[index] || "",
          fireResistanceLevel: fireResistanceLevels[index] || "",
          aboveGroundFloorCount: aboveGroundFloorCounts[index] || "",
          undergroundFloorCount: undergroundFloorCounts[index] || "",
          buildingHeight: buildingHeights[index] || "",
          aboveGroundBuildingArea: aboveGroundBuildingAreas[index] || "",
          undergroundBuildingArea: undergroundBuildingAreas[index] || "",
        })),
        decorationPart: readSingleNamedValue(form, "decorationPart"),
        decorationArea: readSingleNamedValue(form, "decorationArea"),
        decorationFloors: readSingleNamedValue(form, "decorationFloors"),
        insulationMaterialCategory: readSingleNamedValue(form, "insulationMaterialCategory"),
        insulationFloors: readSingleNamedValue(form, "insulationFloors"),
        insulationPart: readSingleNamedValue(form, "insulationPart"),
        insulationMaterial: readSingleNamedValue(form, "insulationMaterial"),
        fireFacilitySystems: formData
          .getAll("fireFacilitySystems")
          .map((value: FormDataEntryValue) => String(value))
          .filter(Boolean),
      });

      if (options?.successMessage) {
        showSaveStatus(options.successMessage, "success");
      }
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "基础信息保存失败，请重试。";
      showSaveStatus(message, "error");
      return false;
    }
  };

  const autosave = debounce(() => {
    saveCurrentForm();
  }, 250);

  form?.addEventListener("input", autosave);
  form?.addEventListener("change", autosave);
  bindDynamicListControls(form, autosave);
  bindBuildingUnitControls(form, projectId, saveCurrentForm);

  document.querySelector<HTMLElement>("[data-action='go-declaration-manage']")?.addEventListener("click", () => {
    const saved = saveCurrentForm();
    if (!saved) {
      return;
    }
    navigate(`#/projects/declaration?id=${projectId}&from=basic-info`);
    void render();
  });

  document.querySelector<HTMLElement>("[data-action='go-receiving']")?.addEventListener("click", () => {
    const saved = saveCurrentForm();
    if (!saved) {
      return;
    }
    navigate(`#/projects/receiving?id=${projectId}`);
  });

  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    saveCurrentForm({ successMessage: "基础信息已保存。" });
  });
}

function bindProjectOverviewPage(projectId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-basic-info']")?.addEventListener("click", () => {
    navigate(`#/projects/basic-info?id=${projectId}`);
  });

  const form = document.querySelector<HTMLFormElement>("#project-overview-form");
  const saveCurrentForm = () => {
    if (!form) {
      return;
    }
    const formData = new FormData(form);
    const buildingUnitIds = Array.from(
      form.querySelectorAll<HTMLInputElement>("input[name='buildingUnitId']"),
    ).map((node) => node.value);
    const buildingNames = readNamedValues(form, "buildingName");
    const structureTypes = readNamedValues(form, "structureType");
    const usageTypes = readNamedValues(form, "usageType");
    const fireResistanceLevels = readNamedValues(form, "fireResistanceLevel");
    const aboveGroundFloorCounts = readNamedValues(form, "aboveGroundFloorCount");
    const undergroundFloorCounts = readNamedValues(form, "undergroundFloorCount");
    const buildingHeights = readNamedValues(form, "buildingHeight");
    const aboveGroundBuildingAreas = readNamedValues(form, "aboveGroundBuildingArea");
    const undergroundBuildingAreas = readNamedValues(form, "undergroundBuildingArea");

    saveProjectOverview(projectId, {
      buildingUnits: buildingUnitIds.map((id, index) => ({
        id,
        buildingName: buildingNames[index] || "",
        structureType: structureTypes[index] || "",
        usageType: usageTypes[index] || "",
        fireResistanceLevel: fireResistanceLevels[index] || "",
        aboveGroundFloorCount: aboveGroundFloorCounts[index] || "",
        undergroundFloorCount: undergroundFloorCounts[index] || "",
        buildingHeight: buildingHeights[index] || "",
        aboveGroundBuildingArea: aboveGroundBuildingAreas[index] || "",
        undergroundBuildingArea: undergroundBuildingAreas[index] || "",
      })),
      decorationPart: readSingleNamedValue(form, "decorationPart"),
      decorationArea: readSingleNamedValue(form, "decorationArea"),
      decorationFloors: readSingleNamedValue(form, "decorationFloors"),
      insulationMaterialCategory: readSingleNamedValue(form, "insulationMaterialCategory"),
      insulationFloors: readSingleNamedValue(form, "insulationFloors"),
      insulationPart: readSingleNamedValue(form, "insulationPart"),
      insulationMaterial: readSingleNamedValue(form, "insulationMaterial"),
      fireFacilitySystems: formData
        .getAll("fireFacilitySystems")
        .map((value: FormDataEntryValue) => String(value))
        .filter(Boolean),
    });
  };

  const autosave = debounce(() => {
    saveCurrentForm();
  }, 250);

  form?.addEventListener("input", autosave);
  form?.addEventListener("change", autosave);

  document.querySelector<HTMLElement>("[data-action='add-building-unit']")?.addEventListener("click", () => {
    const project = getProjectById(projectId);
    if (!project) {
      return;
    }
    saveCurrentForm();
    const latestProject = getProjectById(projectId);
    if (!latestProject) {
      return;
    }
    saveProjectOverview(projectId, {
      ...latestProject.overview,
      buildingUnits: [...latestProject.overview.buildingUnits, createEmptyBuildingUnit()],
    });
    render();
  });

  document.querySelectorAll<HTMLElement>("[data-remove-building-unit]").forEach((node) => {
    node.addEventListener("click", () => {
      const unitId = node.dataset.removeBuildingUnit;
      const project = getProjectById(projectId);
      if (!unitId || !project) {
        return;
      }
      saveCurrentForm();
      const latestProject = getProjectById(projectId);
      if (!latestProject) {
        return;
      }
      saveProjectOverview(projectId, {
        ...latestProject.overview,
        buildingUnits: latestProject.overview.buildingUnits.filter((unit) => unit.id !== unitId),
      });
      render();
    });
  });

  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    saveCurrentForm();
    navigate(`#/projects/home?id=${projectId}`);
  });
}

function bindInspectionItemsPage(projectId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-home']")?.addEventListener("click", () => {
    navigate(`#/projects/home?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='continue-current-item']")?.addEventListener("click", () => {
    const itemId = document.querySelector<HTMLElement>("[data-action='continue-current-item']")?.dataset.itemId;
    if (!itemId) {
      return;
    }
    navigate(`#/projects/inspection-item?projectId=${projectId}&itemId=${itemId}`);
  });

  document.querySelectorAll<HTMLElement>("[data-item-id]").forEach((node) => {
    node.addEventListener("click", () => {
      const itemId = node.dataset.itemId;
      if (!itemId) {
        return;
      }
      navigate(`#/projects/inspection-item?projectId=${projectId}&itemId=${itemId}`);
    });
  });
}

function bindReceivingPage(projectId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-home']")?.addEventListener("click", () => {
    navigate(`#/projects/home?id=${projectId}`);
  });

  const form = document.querySelector<HTMLFormElement>("#receiving-form");
  const galleryInput = document.querySelector<HTMLInputElement>("#receiving-photo-gallery-input");
  const photoStatusNode = document.querySelector<HTMLElement>("#receiving-photo-upload-status");
  const autosave = debounce(() => {
    if (!form) {
      return;
    }
    const project = getProjectById(projectId);
    if (!project) {
      return;
    }

    const formData = new FormData(form);
    saveReceivingData(projectId, {
      acceptanceCode: "",
      acceptanceSummary: "",
      documentBody: String(formData.get("documentBody") || ""),
      photos: project.receiving.photos,
    });
  }, 250);

  form?.addEventListener("input", autosave);
  form?.addEventListener("change", autosave);

  const appendReceivingPhotos = async (input: HTMLInputElement | null, sourceLabel: string) => {
    const project = getProjectById(projectId);
    if (!project || !input?.files?.length) {
      return;
    }

    input.setAttribute("disabled", "true");
    photoStatusNode?.replaceChildren(
      document.createTextNode(`${sourceLabel}处理中，请稍候…（共 ${input.files.length} 张）`),
    );

    try {
      const uploadedPhotos = await readFilesAsDataUrls(input.files);
      saveReceivingData(projectId, {
        ...project.receiving,
        photos: [...project.receiving.photos, ...uploadedPhotos],
      });
      photoStatusNode?.replaceChildren(
        document.createTextNode(`${sourceLabel}已处理完成，已追加 ${uploadedPhotos.length} 张。`),
      );
      render();
    } catch (error) {
      console.error(error);
      photoStatusNode?.replaceChildren(
        document.createTextNode(error instanceof Error ? error.message : "图片处理失败，请重试。"),
      );
    } finally {
      input.value = "";
      input.removeAttribute("disabled");
    }
  };

  galleryInput?.addEventListener("change", async () => {
    await appendReceivingPhotos(galleryInput, "图片");
  });

  document.querySelector<HTMLElement>("[data-action='go-inspection-items']")?.addEventListener("click", () => {
    autosave();
    navigate(`#/projects/inspection-items?id=${projectId}`);
  });
  form?.addEventListener("submit", (event) => {
    event.preventDefault();
    if (!form) {
      return;
    }

    const formData = new FormData(form);
    saveReceivingData(projectId, {
      acceptanceCode: "",
      acceptanceSummary: "",
      documentBody: String(formData.get("documentBody") || ""),
      photos: getProjectById(projectId)?.receiving.photos || [],
    });
    navigate(`#/projects/home?id=${projectId}`);
  });

  document.querySelectorAll<HTMLElement>("[data-receiving-photo-delete]").forEach((node) => {
    node.addEventListener("click", () => {
      const photoIndex = Number(node.dataset.receivingPhotoDelete);
      const project = getProjectById(projectId);
      if (!project || Number.isNaN(photoIndex)) {
        return;
      }
      const removedPhoto = project.receiving.photos[photoIndex];
      void deletePhotoRefs(removedPhoto ? [removedPhoto] : []);
      saveReceivingData(projectId, {
        ...project.receiving,
        photos: project.receiving.photos.filter((_, index) => index !== photoIndex),
      });
      render();
    });
  });

  bindImagePreview();
  bindPhotoReorder(projectId);
}

function bindInspectionItemDetailPage(projectId: string, itemId: string): void {
  document.querySelector<HTMLElement>("[data-action='back-to-items']")?.addEventListener("click", () => {
    navigate(`#/projects/inspection-items?id=${projectId}`);
  });

  document.querySelector<HTMLElement>("[data-action='continue-current-subitem']")?.addEventListener("click", () => {
    const subitemId = document.querySelector<HTMLElement>("[data-action='continue-current-subitem']")?.dataset.subitemId;
    if (!subitemId) {
      return;
    }
    navigate(`#/projects/inspection-subitem?projectId=${projectId}&itemId=${itemId}&subitemId=${subitemId}`);
  });

  document.querySelector<HTMLElement>("[data-action='toggle-item-not-involved']")?.addEventListener("click", () => {
    const currentItem = getInspectionItem(projectId, itemId);
    if (!currentItem) {
      return;
    }
    saveInspectionItemDecision(
      projectId,
      itemId,
      currentItem.decision === "not_involved" ? "pending" : "not_involved",
    );
    render();
  });

  document.querySelectorAll<HTMLElement>("[data-subitem-id]").forEach((node) => {
    node.addEventListener("click", () => {
      const subitemId = node.dataset.subitemId;
      if (!subitemId) {
        return;
      }
      navigate(
        `#/projects/inspection-subitem?projectId=${projectId}&itemId=${itemId}&subitemId=${subitemId}`,
      );
    });
  });
}

function bindInspectionSubitemEditPage(
  projectId: string,
  itemId: string,
  subitemId: string,
): void {
  document.querySelector<HTMLElement>("[data-action='back-to-item-detail']")?.addEventListener("click", () => {
    navigate(`#/projects/inspection-item?projectId=${projectId}&itemId=${itemId}`);
  });

  const form = document.querySelector<HTMLFormElement>("#subitem-form");
  const submitButton = form?.querySelector<HTMLButtonElement>("[type='submit']") || null;
  const photoGalleryInput = document.querySelector<HTMLInputElement>("#photo-gallery-input");
  const photoStatusNode = document.querySelector<HTMLElement>("#subitem-photo-upload-status");
  const buildSubitemPatch = (
    currentSubitem: ReturnType<typeof getInspectionSubitem>,
  ): Pick<
    InspectionSubitemData,
    "sampleCount" | "sampledLocations" | "findings" | "decision" | "photos"
  > | null => {
    if (!form || !currentSubitem) {
      return null;
    }

    const formData = new FormData(form);
    const decision = String(formData.get("decision") || "pending") as
      | "pending"
      | "pass"
      | "fail"
      | "not_involved";

    return {
      sampleCount: decision === "not_involved" ? "" : String(formData.get("sampleCount") || ""),
      sampledLocations:
        decision === "not_involved" ? "" : String(formData.get("sampledLocations") || ""),
      findings: decision === "not_involved" ? "" : String(formData.get("findings") || ""),
      decision,
      photos: decision === "not_involved" ? [] : currentSubitem.photos,
    };
  };
  const autosave = debounce(() => {
    if (!form) {
      return;
    }
    const currentSubitem = getInspectionSubitem(projectId, itemId, subitemId);
    if (!currentSubitem) {
      return;
    }

    const patch = buildSubitemPatch(currentSubitem);
    if (!patch) {
      return;
    }
    saveInspectionSubitem(projectId, itemId, subitemId, patch);
  }, 250);

  form?.addEventListener("input", autosave);
  form?.addEventListener("change", autosave);

  const appendSubitemPhotos = async (input: HTMLInputElement | null, sourceLabel: string) => {
    const currentSubitem = getInspectionSubitem(projectId, itemId, subitemId);
    if (!currentSubitem || !input?.files?.length) {
      return;
    }

    const patch = buildSubitemPatch(currentSubitem);
    if (!patch) {
      return;
    }

    input.setAttribute("disabled", "true");
    photoStatusNode?.replaceChildren(
      document.createTextNode(`${sourceLabel}处理中，请稍候…（共 ${input.files.length} 张）`),
    );

    try {
      const uploadedPhotos = await readFilesAsDataUrls(input.files);
      photoStatusNode?.replaceChildren(
        document.createTextNode(`${sourceLabel}已处理完成，已追加 ${uploadedPhotos.length} 张。`),
      );
      saveInspectionSubitem(projectId, itemId, subitemId, {
        ...patch,
        photos: patch.decision === "not_involved" ? [] : [...currentSubitem.photos, ...uploadedPhotos],
      });
      render();
    } catch (error) {
      console.error(error);
      photoStatusNode?.replaceChildren(
        document.createTextNode(error instanceof Error ? error.message : "图片处理失败，请重试。"),
      );
    } finally {
      input.value = "";
      input.removeAttribute("disabled");
    }
  };

  photoGalleryInput?.addEventListener("change", async () => {
    await appendSubitemPhotos(photoGalleryInput, "图片");
  });
  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentSubitem = getInspectionSubitem(projectId, itemId, subitemId);
    if (!currentSubitem) {
      return;
    }
    submitButton?.setAttribute("disabled", "true");
    const originalLabel = submitButton?.textContent || "保存子项";
    if (submitButton) {
      submitButton.textContent = "保存中...";
    }
    try {
      const patch = buildSubitemPatch(currentSubitem);
      if (!patch) {
        return;
      }
      if (patch.decision === "not_involved" && currentSubitem.photos.length) {
        void deletePhotoRefs(currentSubitem.photos);
      }
      saveInspectionSubitem(projectId, itemId, subitemId, patch);
      window.setTimeout(() => {
        navigate(`#/projects/inspection-item?projectId=${projectId}&itemId=${itemId}`);
      }, 0);
    } catch (error) {
      console.error(error);
      const status = document.createElement("p");
      status.className = "mini-meta";
      status.textContent = error instanceof Error ? error.message : "保存子项失败，请重试。";
      form.appendChild(status);
    } finally {
      submitButton?.removeAttribute("disabled");
      if (submitButton) {
        submitButton.textContent = originalLabel;
      }
    }
  });

  document.querySelectorAll<HTMLElement>("[data-photo-delete]").forEach((node) => {
    node.addEventListener("click", () => {
      const photoIndex = Number(node.dataset.photoDelete);
      const currentSubitem = getInspectionSubitem(projectId, itemId, subitemId);
      if (!currentSubitem || Number.isNaN(photoIndex)) {
        return;
      }
      const removedPhoto = currentSubitem.photos[photoIndex];
      void deletePhotoRefs(removedPhoto ? [removedPhoto] : []);

      saveInspectionSubitem(projectId, itemId, subitemId, {
        sampleCount: currentSubitem.sampleCount,
        sampledLocations: currentSubitem.sampledLocations,
        findings: currentSubitem.findings,
        decision: currentSubitem.decision,
        photos: currentSubitem.photos.filter((_, index) => index !== photoIndex),
      });

      render();
    });
  });

  bindImagePreview();
  bindPhotoReorder(projectId, itemId, subitemId);
}

async function readFilesAsDataUrls(fileList: FileList | null | undefined): Promise<string[]> {
  if (!fileList || fileList.length === 0) {
    return [];
  }

  const files = Array.from(fileList);
  const dataUrls = await Promise.all(files.map((file) => readFileAsDataUrl(file)));
  const persistTargets = dataUrls.filter((value) => value.startsWith("data:image/") || value.startsWith("data:application/pdf"));
  const persistedRefs = persistTargets.length ? await persistImageDataUrls(persistTargets) : [];
  const persistedMap = new Map<string, string>();
  persistTargets.forEach((value, index) => {
    persistedMap.set(value, persistedRefs[index] || value);
  });

  return dataUrls.map((value) => persistedMap.get(value) || value).filter(Boolean);
}

function bindImagePreview(): void {
  document.querySelectorAll<HTMLElement>("[data-preview-image]").forEach((node) => {
    if (node.dataset.bound === "true") {
      return;
    }
    node.dataset.bound = "true";
    node.addEventListener("click", (event) => {
      event.preventDefault();
      const imageUrl = node.dataset.previewImage;
      const title = node.dataset.previewTitle || "图片预览";
      if (!imageUrl) {
        return;
      }
      openImagePreview(imageUrl, title);
    });
  });
}

function bindPhotoReorder(projectId: string, itemId?: string, subitemId?: string): void {
  const cards = Array.from(document.querySelectorAll<HTMLElement>(".reorderable-photo-card"));
  if (!cards.length) {
    return;
  }

  let activeCard: HTMLElement | null = null;
  let activeScope = "";
  let holdTimer: number | null = null;
  let dragging = false;

  const clearHoldTimer = () => {
    if (holdTimer !== null) {
      window.clearTimeout(holdTimer);
      holdTimer = null;
    }
  };

  const finishReorder = () => {
    clearHoldTimer();
    if (!activeCard) {
      return;
    }

    activeCard.classList.remove("is-dragging");
    const changed = dragging;
    const scope = activeScope;
    activeCard = null;
    activeScope = "";
    dragging = false;

    if (!changed) {
      return;
    }

    const orderedKeys = Array.from(
      document.querySelectorAll<HTMLElement>(`.reorderable-photo-card[data-photo-scope='${scope}']`),
    )
      .map((node) => node.dataset.photoKey || "")
      .filter(Boolean);

    savePhotoOrder(projectId, scope, orderedKeys, itemId, subitemId);
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (!activeCard || !dragging) {
      return;
    }

    event.preventDefault();
    const overElement = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>(".reorderable-photo-card");
    if (!overElement || overElement === activeCard || overElement.dataset.photoScope !== activeScope) {
      return;
    }

    const bounds = overElement.getBoundingClientRect();
    const shouldInsertAfter =
      bounds.width > bounds.height
        ? event.clientX > bounds.left + bounds.width / 2
        : event.clientY > bounds.top + bounds.height / 2;

    const parent = overElement.parentElement;
    if (!parent) {
      return;
    }

    parent.insertBefore(activeCard, shouldInsertAfter ? overElement.nextSibling : overElement);
  };

  const handlePointerUp = () => {
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);
    finishReorder();
  };

  cards.forEach((card) => {
    if (card.dataset.boundReorder === "true") {
      return;
    }
    card.dataset.boundReorder = "true";

    card.addEventListener("pointerdown", (event) => {
      if ((event.target as HTMLElement).closest(".photo-delete-button")) {
        return;
      }

      activeCard = card;
      activeScope = card.dataset.photoScope || "";
      clearHoldTimer();
      holdTimer = window.setTimeout(() => {
        if (!activeCard) {
          return;
        }
        dragging = true;
        activeCard.classList.add("is-dragging");
      }, 180);

      window.addEventListener("pointermove", handlePointerMove, { passive: false });
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    });
  });
}

function savePhotoOrder(
  projectId: string,
  scope: string,
  orderedKeys: string[],
  itemId?: string,
  subitemId?: string,
): void {
  if (scope === "receiving") {
    const project = getProjectById(projectId);
    if (!project) {
      return;
    }
    saveReceivingData(projectId, {
      ...project.receiving,
      photos: orderedKeys,
    });
    render();
    return;
  }

  if (scope === "declaration") {
    const project = getProjectById(projectId);
    if (!project) {
      return;
    }
    const photoNameMap = new Map(project.declaration.photoDataUrls.map((photo, index) => [photo, project.declaration.photoNames[index] || ""]));
    saveDeclarationData(projectId, {
      ...project.declaration,
      photoDataUrls: orderedKeys,
      photoNames: orderedKeys.map((photo) => photoNameMap.get(photo) || ""),
    });
    render();
    return;
  }

  if (scope === "subitem" && itemId && subitemId) {
    const currentSubitem = getInspectionSubitem(projectId, itemId, subitemId);
    if (!currentSubitem) {
      return;
    }
    saveInspectionSubitem(projectId, itemId, subitemId, {
      sampleCount: currentSubitem.sampleCount,
      sampledLocations: currentSubitem.sampledLocations,
      findings: currentSubitem.findings,
      decision: currentSubitem.decision,
      photos: orderedKeys,
    });
    render();
  }
}

function openImagePreview(imageUrl: string, title: string): void {
  closeImagePreview();
  const modal = document.createElement("div");
  modal.className = "image-preview-modal";
  modal.id = "image-preview-modal";
  modal.innerHTML = `
    <div class="image-preview-dialog">
      <div class="inline-head">
        <strong>${title}</strong>
        <button type="button" class="ghost-button small-button" data-action="close-image-preview">关闭</button>
      </div>
      <img src="${imageUrl}" alt="${title}" />
    </div>
  `;
  modal.addEventListener("click", (event) => {
    if (event.target === modal) {
      closeImagePreview();
    }
  });
  document.body.appendChild(modal);
  modal.querySelector<HTMLElement>("[data-action='close-image-preview']")?.addEventListener("click", () => {
    closeImagePreview();
  });
}

function closeImagePreview(): void {
  document.querySelector<HTMLElement>("#image-preview-modal")?.remove();
}

async function readFileAsDataUrl(file: File): Promise<string> {
  if (file.type.startsWith("image/")) {
    try {
      return await compressImageFile(file);
    } catch (error) {
      console.warn("Image compression failed, falling back to raw read.", error);
    }
  }

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });
}

async function compressImageFile(file: File): Promise<string> {
  const image = await loadImageFromFile(file);
  const longestSide = Math.max(image.naturalWidth, image.naturalHeight) || 1;
  const scale = Math.min(1, 1280 / longestSide);
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas context unavailable.");
  }

  context.drawImage(image, 0, 0, width, height);

  let quality = 0.82;
  let dataUrl = canvas.toDataURL("image/jpeg", quality);
  const maxLength = 240 * 1024;

  while (dataUrl.length > maxLength && quality > 0.42) {
    quality -= 0.08;
    dataUrl = canvas.toDataURL("image/jpeg", quality);
  }

  return dataUrl;
}

async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(file);

  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Failed to decode image: ${file.name}`));
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function readSingleNamedValue(root: ParentNode, name: string): string {
  return String((root.querySelector(`[name='${name}']`) as HTMLInputElement | HTMLTextAreaElement | null)?.value || "");
}

function readNamedValues(root: ParentNode, name: string): string[] {
  const values = Array.from(
    root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(`[name='${name}']`),
  )
    .map((node) => node.value.trim())
    .filter((value, index, all) => value || all.length === 1 || index < all.length - 1);

  return values.length ? values : [""];
}

function bindDynamicListControls(
  form: HTMLFormElement | null,
  autosave: () => void,
): void {
  if (!form) {
    return;
  }

  const addRow = (listName: string, placeholder: string) => {
    const list = form.querySelector<HTMLElement>(`[data-list-name='${listName}']`);
    if (!list) {
      return;
    }

    const row = document.createElement("div");
    row.className = "dynamic-list-row";
    row.innerHTML = `
      <input name="${listName}" type="text" placeholder="${placeholder}" />
      <button class="ghost-button small-button" type="button" data-remove-list-row="${listName}">删除</button>
    `;
    list.appendChild(row);
    bindDynamicListControls(form, autosave);
    autosave();
  };

  const bindAdd = (selector: string, listName: string, placeholder: string) => {
    const button = document.querySelector<HTMLElement>(selector);
    if (!button || button.dataset.bound === "true") {
      return;
    }
    button.dataset.bound = "true";
    button.addEventListener("click", () => {
      addRow(listName, placeholder);
    });
  };

  bindAdd("[data-action='add-construction-unit']", "constructionUnits", "填写建设单位");
  bindAdd("[data-action='add-design-company']", "designCompanies", "填写设计企业");
  bindAdd("[data-action='add-construction-company']", "constructionCompanies", "填写施工企业");
  bindAdd("[data-action='add-supervision-company']", "supervisionCompanies", "填写监理企业");

  form.querySelectorAll<HTMLElement>("[data-remove-list-row]").forEach((node) => {
    if (node.dataset.bound === "true") {
      return;
    }
    node.dataset.bound = "true";
    node.addEventListener("click", () => {
      const row = node.closest(".dynamic-list-row");
      row?.remove();
      autosave();
    });
  });
}

function bindBuildingUnitControls(
  form: HTMLFormElement | null,
  projectId: string,
  saveCurrentForm: () => void,
): void {
  if (!form) {
    return;
  }

  const addButton = document.querySelector<HTMLElement>("[data-action='add-building-unit']");
  if (addButton && addButton.dataset.bound !== "true") {
    addButton.dataset.bound = "true";
    addButton.addEventListener("click", () => {
      saveCurrentForm();
      const latestProject = getProjectById(projectId);
      if (!latestProject) {
        return;
      }
      saveProjectOverview(projectId, {
        ...latestProject.overview,
        buildingUnits: [...latestProject.overview.buildingUnits, createEmptyBuildingUnit()],
      });
      render();
    });
  }

  form.querySelectorAll<HTMLElement>("[data-remove-building-unit]").forEach((node) => {
    if (node.dataset.bound === "true") {
      return;
    }
    node.dataset.bound = "true";
    node.addEventListener("click", () => {
      const unitId = node.dataset.removeBuildingUnit;
      if (!unitId) {
        return;
      }
      saveCurrentForm();
      const latestProject = getProjectById(projectId);
      if (!latestProject) {
        return;
      }
      saveProjectOverview(projectId, {
        ...latestProject.overview,
        buildingUnits: latestProject.overview.buildingUnits.filter((unit) => unit.id !== unitId),
      });
      render();
    });
  });
}

function debounce<T extends (...args: never[]) => void>(fn: T, waitMs: number): T {
  let timer: number | null = null;
  return ((...args: never[]) => {
    if (timer !== null) {
      window.clearTimeout(timer);
    }
    timer = window.setTimeout(() => fn(...args), waitMs);
  }) as T;
}

function applyDeclarationRecognition(projectId: string): void {
  const project = getProjectById(projectId);
  if (!project) {
    return;
  }

  const { extractedBasicInfo, extractedOverview } = project.declaration.recognition;
  if (!Object.keys(extractedBasicInfo).length && !Object.keys(extractedOverview).length) {
    return;
  }

  saveProjectBasicInfo(projectId, {
    ...buildEmptyRecognitionBasicInfo(),
    ...extractedBasicInfo,
    constructionNature: Array.isArray(extractedBasicInfo.constructionNature) ? extractedBasicInfo.constructionNature : [],
    constructionUnits:
      Array.isArray(extractedBasicInfo.constructionUnits) && extractedBasicInfo.constructionUnits.length
        ? extractedBasicInfo.constructionUnits
        : [""],
    designCompanies:
      Array.isArray(extractedBasicInfo.designCompanies) && extractedBasicInfo.designCompanies.length
        ? extractedBasicInfo.designCompanies
        : [""],
    constructionCompanies:
      Array.isArray(extractedBasicInfo.constructionCompanies) && extractedBasicInfo.constructionCompanies.length
        ? extractedBasicInfo.constructionCompanies
        : [""],
    supervisionCompanies:
      Array.isArray(extractedBasicInfo.supervisionCompanies) && extractedBasicInfo.supervisionCompanies.length
        ? extractedBasicInfo.supervisionCompanies
        : [""],
  });

  saveProjectOverview(projectId, {
    ...buildEmptyRecognitionOverview(project.overview.fireFacilitySystems),
    ...extractedOverview,
    fireFacilitySystems: [...project.overview.fireFacilitySystems],
    buildingUnits:
      Array.isArray(extractedOverview.buildingUnits) && extractedOverview.buildingUnits.length
        ? extractedOverview.buildingUnits
        : [createEmptyBuildingUnit()],
  });

  saveDeclarationData(projectId, {
    ...project.declaration,
    parseStatus: "recognized",
    recognition: {
      ...project.declaration.recognition,
      status: "recognized",
      summary: "识别完成，已自动写入项目基本信息与项目概况，请继续人工校对。",
      updatedAt: new Date().toLocaleString("zh-CN", { hour12: false }),
    },
  });
  navigate(`#/projects/basic-info?id=${projectId}`);
}

async function runDeclarationRecognition(projectId: string): Promise<void> {
  const project = getProjectById(projectId);
  if (!project) {
    return;
  }

  if (!project.declaration.pdfFileDataUrl && project.declaration.photoDataUrls.length === 0) {
    return;
  }

  try {
    saveDeclarationData(projectId, {
      ...project.declaration,
      parseStatus: "running",
      recognition: {
        ...project.declaration.recognition,
        status: "running",
        summary: "本地识别处理中，请稍候……（通常为 1-3 分钟）",
        updatedAt: new Date().toLocaleString("zh-CN", { hour12: false }),
      },
    });
    render();
  } catch (error) {
    window.alert(error instanceof Error ? error.message : "无法开始识别，请稍后重试。");
    return;
  }

  try {
    await preloadPhotoRefs([project.declaration.pdfFileDataUrl, ...project.declaration.photoDataUrls].filter(Boolean));
    const response = await fetch("/api/recognize/declaration", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        pdfFileDataUrl: resolvePhotoUrl(project.declaration.pdfFileDataUrl),
        photoDataUrls: project.declaration.photoDataUrls.map((photo) => resolvePhotoUrl(photo)).filter(Boolean),
        generatedAt: new Date().toLocaleString("zh-CN", { hour12: false }),
      }),
    });

    const result = await response.json();
    const latestProject = getProjectById(projectId);
    if (!latestProject) {
      return;
    }

    saveDeclarationData(projectId, {
      ...latestProject.declaration,
      parseStatus: result.status,
      recognition: result,
    });

    if (result.status === "recognized") {
      applyDeclarationRecognition(projectId);
      return;
    }
    render();
  } catch (error) {
    const latestProject = getProjectById(projectId);
    if (!latestProject) {
      return;
    }
    saveDeclarationData(projectId, {
      ...latestProject.declaration,
      parseStatus: "failed",
      recognition: {
        ...latestProject.declaration.recognition,
        status: "failed",
        summary: error instanceof Error ? error.message : "本地识别失败，请稍后重试。",
        updatedAt: new Date().toLocaleString("zh-CN", { hour12: false }),
      },
    });
    render();
  }
}

async function exportProjectWord(projectId: string, kind: "report" | "record"): Promise<void> {
  const statusNode = document.querySelector<HTMLElement>("#preview-export-status");
  const emailInput = document.querySelector<HTMLInputElement>("#preview-email-input");
  const sendButton = document.querySelector<HTMLButtonElement>("[data-action='send-export-email']");
  const project = getProjectById(projectId);
  if (!project) {
    return;
  }
  const readiness = buildExportReadiness(project, getProjectPendingSyncCount(projectId));
  if (!readiness.outputAllowed) {
    statusNode?.replaceChildren(document.createTextNode(readiness.outputBlockedReason));
    return;
  }
  try {
    statusNode?.replaceChildren(
      document.createTextNode(
        kind === "report"
          ? "正在生成正式报告（Word），请稍后......生成过程大约持续 1-3 分钟。"
          : "正在生成正式记录表（Word），请稍后......生成过程大约持续 1-3 分钟。",
      ),
    );

    const { blob, exportRef, fileName } = await requestWordExport(projectId, kind, { preview: false });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);

    markExportGenerated(projectId, kind, { exportRef, fileName });
    const latestProject = getProjectById(projectId);
    const emailAllowed = Boolean(latestProject?.exportStatus.reportGeneratedAt) && Boolean(latestProject?.exportStatus.recordGeneratedAt);

    if (emailAllowed) {
      emailInput?.removeAttribute("disabled");
      sendButton?.removeAttribute("disabled");
      statusNode?.replaceChildren(document.createTextNode("报告和记录表已生成完毕，可以填写邮箱并发送下载链接。"));
    } else {
      statusNode?.replaceChildren(
        document.createTextNode(
          kind === "report"
            ? "正式报告生成完毕。请继续生成正式记录表。"
            : "正式记录表生成完毕。请继续生成正式报告。",
        ),
      );
    }
    render();
  } catch (error) {
    statusNode?.replaceChildren(
      document.createTextNode(error instanceof Error ? error.message : "生成失败，请稍后重试。"),
    );
  }
}

function clearProjectExport(projectId: string, kind: "report" | "record"): void {
  const statusNode = document.querySelector<HTMLElement>("#preview-export-status");
  const emailInput = document.querySelector<HTMLInputElement>("#preview-email-input");
  const sendButton = document.querySelector<HTMLButtonElement>("[data-action='send-export-email']");

  clearExportGenerated(projectId, kind);

  const latestProject = getProjectById(projectId);
  const emailAllowed = Boolean(latestProject?.exportStatus.reportGeneratedAt) && Boolean(latestProject?.exportStatus.recordGeneratedAt);
  if (!emailAllowed) {
    emailInput?.setAttribute("disabled", "disabled");
    sendButton?.setAttribute("disabled", "disabled");
  }

  statusNode?.replaceChildren(
    document.createTextNode(
      kind === "report"
        ? "已删除当前报告生成记录，可以重新生成正式报告。"
        : "已删除当前记录表生成记录，可以重新生成正式记录表。",
    ),
  );
  render();
}

async function syncCurrentProject(projectId: string): Promise<void> {
  const project = getProjectById(projectId);
  const { user } = getState();
  if (!project || !user) {
    return;
  }

  try {
    await syncProjectSnapshot({
      projectId,
      actor: {
        id: user.id,
        name: user.name,
        role: user.role,
        groupId: user.groupId,
        groupName: user.groupName,
      },
      pendingChanges: getPendingSyncChanges(projectId),
      project,
    });
    markProjectSynced(projectId);
    await render();
    window.alert("当前项目已同步到服务端。");
  } catch (error) {
    window.alert(error instanceof Error ? error.message : "同步失败，请稍后重试。");
  }
}

async function sendExportEmail(projectId: string): Promise<void> {
  const project = getProjectById(projectId);
  if (!project) {
    return;
  }
  const readiness = buildExportReadiness(project, getProjectPendingSyncCount(projectId));
  const emailInput = document.querySelector<HTMLInputElement>("#preview-email-input");
  const receiver = String(emailInput?.value || "").trim();
  const statusNode = document.querySelector<HTMLElement>("#preview-export-status");
  const reportReady = Boolean(project.exportStatus.reportGeneratedAt);
  const recordReady = Boolean(project.exportStatus.recordGeneratedAt);
  if (!readiness.outputAllowed) {
    statusNode?.replaceChildren(document.createTextNode(readiness.outputBlockedReason));
    return;
  }
  if (!reportReady || !recordReady) {
    statusNode?.replaceChildren(document.createTextNode("请先分别成功生成报告和记录表，再发送到邮箱。"));
    return;
  }
  if (!receiver) {
    statusNode?.replaceChildren(document.createTextNode("请先填写接收邮箱地址。"));
    return;
  }

  const generatedAt = new Date().toISOString().slice(0, 19).replace("T", " ");
  const basePayload = await buildResolvedExportPayload(project, generatedAt);
  const payload = {
    receiver,
    downloadBaseUrl: window.location.origin,
    ...basePayload,
    reportFileName: project.exportStatus.reportFileName || `${basePayload.projectName}-消防验收报告.docx`,
    recordFileName: project.exportStatus.recordFileName || `${basePayload.projectName}-现场评定记录表.docx`,
    reportExportRef: project.exportStatus.reportExportRef,
    recordExportRef: project.exportStatus.recordExportRef,
  };

  statusNode?.replaceChildren(document.createTextNode("正在生成下载链接并发送邮件，请稍候..."));

  try {
    const response = await fetch("/api/export/email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const result = (await response.json()) as { status?: string; summary?: string };
    if (!response.ok || result.status === "failed") {
      throw new Error(result.summary || "发送邮件失败，请稍后重试。");
    }

    statusNode?.replaceChildren(
      document.createTextNode(result.summary || `邮件已发送到 ${receiver}。`),
    );
  } catch (error) {
    statusNode?.replaceChildren(
      document.createTextNode(error instanceof Error ? error.message : "发送邮件失败，请稍后重试。"),
    );
  }
}

function getExportPayload(
  basePayload: Awaited<ReturnType<typeof buildResolvedExportPayload>>,
  kind: "report" | "record",
  preview: boolean,
) {
  if (kind === "report") {
    return {
      title: "建设工程消防验收技术服务报告",
      ...basePayload,
      fileName: buildExportFileName(basePayload.projectName, "report", preview),
    };
  }

  return {
    title: "建设工程现场评定记录表",
    ...basePayload,
    fileName: buildExportFileName(basePayload.projectName, "record", preview),
  };
}

async function requestWordExport(
  projectId: string,
  kind: "report" | "record",
  options?: { preview?: boolean },
): Promise<{ blob: Blob; exportRef: string; fileName: string }> {
  const project = getProjectById(projectId);
  if (!project) {
    throw new Error("未找到当前项目。");
  }

  const generatedAt = new Date().toLocaleString("zh-CN", { hour12: false });
  const basePayload = await buildResolvedExportPayload(project, generatedAt);
  const payload = getExportPayload(basePayload, kind, Boolean(options?.preview));
  const endpoint = kind === "report" ? "/api/export/report" : "/api/export/record";
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await response.text());
  }

  const blob = await response.blob();
  const exportRef = decodeURIComponent(response.headers.get("X-Export-Ref") || "");
  const fileName = decodeURIComponent(response.headers.get("X-Export-File-Name") || payload.fileName);

  return { blob, exportRef, fileName };
}

async function ensurePreviewExport(
  projectId: string,
  kind: "report" | "record",
  force = false,
): Promise<{ exportRef: string; fileName: string }> {
  const project = getProjectById(projectId);
  if (!project) {
    throw new Error("未找到当前项目。");
  }

  const existingRef = kind === "report" ? project.exportStatus.reportPreviewRef : project.exportStatus.recordPreviewRef;
  const existingFileName =
    kind === "report" ? project.exportStatus.reportPreviewFileName : project.exportStatus.recordPreviewFileName;

  if (!force && existingRef) {
    return { exportRef: existingRef, fileName: existingFileName };
  }

  const { exportRef, fileName } = await requestWordExport(projectId, kind, { preview: true });
  markPreviewGenerated(projectId, kind, { exportRef, fileName });
  return { exportRef, fileName };
}

async function renderGeneratedDocxPreview(
  projectId: string,
  kind: "report" | "record",
  force = false,
): Promise<void> {
  const statusNode = document.querySelector<HTMLElement>("#generated-preview-status");
  const frameNode = document.querySelector<HTMLElement>("#generated-docx-preview");
  if (!statusNode || !frameNode) {
    return;
  }

  statusNode.textContent = `${getPreviewHeading(kind)}生成中，请稍候...`;
  frameNode.innerHTML = "";

  try {
    const { exportRef, fileName } = await ensurePreviewExport(projectId, kind, force);
    const response = await fetch(getExportDownloadUrl(exportRef), {
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error("无法加载当前成品预览。");
    }

    const buffer = await response.arrayBuffer();
    await renderDocxAsync(buffer, frameNode, undefined, {
      className: "docx-preview-page",
      ignoreWidth: false,
      ignoreHeight: false,
      inWrapper: true,
      breakPages: true,
      ignoreLastRenderedPageBreak: false,
      useBase64URL: true,
    });
    window.requestAnimationFrame(() => {
      fitGeneratedPreviewToViewport(frameNode);
      bindGeneratedPreviewResize(frameNode);
    });
    statusNode.textContent = `${getPreviewHeading(kind)}已更新：${fileName}`;
  } catch (error) {
    frameNode.innerHTML = "";
    statusNode.textContent = error instanceof Error ? error.message : "成品预览生成失败，请稍后重试。";
  }
}

function bindNetworkEvents(): void {
  window.addEventListener("online", () => {
    render();
  });

  window.addEventListener("offline", () => {
    render();
  });

  window.addEventListener("focus", () => {
    render();
  });

  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      render();
    }
  });
}

export function startApp(): void {
  const app = document.querySelector<HTMLElement>("#app");
  if (app) {
    app.innerHTML = `
      <section class="screen">
        <div class="hero-card">
          <p class="eyebrow">消防验收报告助手</p>
          <h1>正在连接服务器</h1>
          <p class="muted">正在读取用户和项目数据，请稍后。</p>
        </div>
      </section>
    `;
  }
  void initializeServerState()
    .then(() => migrateLegacyPhotoStorage())
    .then(() => render());
  window.addEventListener("hashchange", () => {
    void render();
  });
  bindNetworkEvents();
}
